import {
  Button,
  Checkbox,
  ContentLoader,
  ExpanderPanel,
  NotFoundState,
  RadioButton, Spinner,
  TableSearch,
  TreeSummary,
  TreeSummaryRow,
  TypeCounter,
} from "@rio-cloud/rio-uikit";
import React, { useEffect, useMemo, useState } from "react";
import { TableVirtuoso } from "react-virtuoso";

import SortArrows from "@rio-cloud/rio-uikit/SortArrows";
import { useAppSelector } from "@store";
import Paginator from "src/components/Paginator";
import { IVehicle } from "src/screens/tripsListRoute";
import { fetchVehiclesWithTags } from "src/services/routes";
import Header, { TDirection } from "./components/header";

enum VehicleType {
  TRUCK = "Caminhão",
  BUS = "Ônibus",
  TRAILER = "Carreta",
  VAN = "Van",
}

interface Props {
  open: boolean;
  onClose: () => void;
  vehicles: Array<IVehicle>;
  handleSaveVehicle: (idVehicle: string, name: string) => void;
  onVehicleSelectionChange: (idVehicle: string, name: string) => void;
  handleCancel: () => void;
  onChangeInput: (text: string) => void;
  inputValue: string;
  page: number;
  pages: number;
  pageSize: number;
  handlePage: (params: { page: number, pages?: number }) => void;
  fleets?: { [x: string]: string; };
  initialSlice: number;
  endSlice: number;
  stateTrip?: any;
  statusTrip?: string;
  vehicleTotals?: { [key: string]: number };
  onLoadMoreTags?: () => void;
  hasMoreTags?: boolean;
  onFilterByType?: (vehicleType: string | null) => void;
  currentVehicleTypeFilter?: string | null;
  isLoading: boolean;
  searchLoading: boolean;
}


const AddVehicle = ({
  open,
  onClose,
  vehicles,
  handleSaveVehicle,
  onVehicleSelectionChange,
  handleCancel,
  inputValue,
  onChangeInput,
  handlePage,
  page,
  pages,
  fleets,
  initialSlice = 0,
  pageSize = 10,
  vehicleTotals,
  onLoadMoreTags,
  hasMoreTags,
  onFilterByType,
  currentVehicleTypeFilter,
  isLoading,
  searchLoading,
}: Props) => {

  const getTotalVehiclesForGroup = (fleet: [string, IVehicle[]]) => {
    const groupKey = fleet[0];
    
    if(vehicleTotals && vehicleTotals[groupKey.toLowerCase()] !== undefined) {
      return vehicleTotals[groupKey.toLowerCase()];
    }
    
    const groupData = groupPagination[groupKey];
    if (groupData?.totalVehicles !== undefined) {
      return groupData.totalVehicles;
    }

    if (groupTotalVehicles[groupKey] !== undefined) {
      return groupTotalVehicles[groupKey];
    }


    return fleet[1].length;
  };

  const renderExpanderPanelTitle = (fleet: [string, IVehicle[]]) => (
    <table className="table table-layout-fixed width-100pct bg-lightest rounded-small">
      <thead>
        <tr>
          <th colSpan={7} scope="col">
            <span className="text-light text-size-14 text-capitalize text-color-dark">
              {fleet[0]?.toLocaleLowerCase()}
            </span>
          </th>
          <th scope="col">
            <div
              className="bg-white width-45 height-22 display-flex align-items-center justify-content-center rounded"
              style={{ border: "1px solid var(--gray-light)" }}
            >
              <span className="text-light text-size-12 text-capitalize text-color-dark">
                {getTotalVehiclesForGroup(fleet)}
              </span>
            </div>
          </th>
        </tr>
      </thead>
    </table>
  );

  const renderVehicleRow = (index: number, detail: IVehicle) => (
    <>
      <td key={"select"} data-field={"select"}>
        <RadioButton
          name="radios"
          id={detail.name}
          checked={detail.id === selected}
          onChange={() => handleChangeSelect(detail)}
        />
      </td>
      <td key={"type"} data-field={"type"}>
        <div>
          <span className="text-size-12 text-normal text-color-darker text-light margin-left-10">
            {
              VehicleType[
              detail.type?.toLocaleUpperCase() as keyof typeof VehicleType
              ]
            }
          </span>
        </div>
      </td>
      <td key={"vehicle"} data-field={"vehicle"}>
        <span className="text-size-12 text-normal text-color-darker text-light margin-left-10">
          {detail.name}
        </span>
      </td>
      <td key={"odometer"} data-field={"odometer"}>
        <span className="text-size-12 text-normal text-color-darker text-light margin-left-10">{detail?.odometer ? detail.odometer + 'km' : '-'}</span>
      </td>
      <td key={"hourmeter"} data-field={"hourmeter"}>
        <span className="text-size-12 text-normal text-color-darker text-light margin-left-10"> {detail?.hourmeter ? detail?.hourmeter + 'h' : '-'}</span>
      </td>
      <td key={"location"} data-field={"location"}>
        {detail?.latitude && detail?.longitude ? (
          <span className="text-size-12 text-normal text-color-darker text-light margin-left-10">
            {`${detail?.latitude?.toString()?.slice(0, 10)}, ${detail?.longitude?.toString()?.slice(0, 10)}`}
          </span>
        ) : <span className="text-size-12 text-normal text-color-darker text-light margin-left-10">-</span>}
      </td>
    </>
  );

  const renderExpanderPanelContent = (fleet: [string, IVehicle[]]) => {
    const groupKey = fleet[0];
    const groupData = groupPagination[groupKey];
    const initialVehicles = fleet[1].slice(0, 10);
    const vehiclesToShow = groupData?.vehicles || initialVehicles;
    const totalVehicles = getTotalVehiclesForGroup(fleet);
    const totalPages = Math.ceil(totalVehicles / 10) || 1;
    const currentPage = groupData?.page || 1;
    const hasMore = currentPage < totalPages || (groupData?.hasMore ?? (totalPages > 1));
    const isLoading = groupData?.loading || false;

    const rowHeight = 60;
    const headerHeight = 60;
    const calculatedHeight = Math.min(350, headerHeight + (vehiclesToShow.length * rowHeight));

    return (
      <>
        <TableVirtuoso
          style={{ height: `${calculatedHeight}px` }}
          data={vehiclesToShow}
          endReached={async () => {
            const tagId = groupTagMapping[groupKey];
            if (tagId && hasMore && !isLoading) {
              await fetchVehiclesForTag(tagId, groupKey, currentPage + 1, 10, inputValue);
            }
          }}
          overscan={200}
          components={{
            Table: ({ style, ...props }) => <table {...props} style={{ ...style, tableLayout: 'fixed' }} className="table table-head-filled margin-top-4 bg-white" />,
            TableBody: React.forwardRef(({ style, ...props }, ref) => <tbody {...props} ref={ref} style={{ ...style }} />),
            TableRow: ({ item, ...props }) => <tr {...props} className="height-60" />,
          }}
          fixedHeaderContent={() => (
            <Header 
              directions={directions} 
              header={header} 
              onClick={handleDrection} 
            />
          )}
          itemContent={renderVehicleRow}
        />
        {isLoading && (
          <div className="text-align-center padding-10 bg-white">
            <Spinner />
          </div>
        )}
      </>
    );
  };

  const renderVehicleExpanderPanel = (fleet: [string, IVehicle[]], idx: number) => {
    return (
      <ExpanderPanel
        key={idx}
        onToggle={(toggle) => {
          if (toggle) {
            setOpenPanelIndex(idx);
          } else if (openPanelIndex === idx) {
            setOpenPanelIndex(null);
          }
        }}
        open={openPanelIndex === idx}
        title={renderExpanderPanelTitle(fleet)}
        headerClassName="padding-0 position-relative"
        titleClassName="width-100pct bg-lightest border-radius-2 height-60 display-flex border-radius-none"
        bodyClassName="padding-0 height-100pct"
        className="shadow-default margin-bottom-4 border-radius-none  text-color-black"
        iconClassName="position-absolute top-0 right-15 height-100pct display-flex align-items-center text-color-dark"
      >
        {renderExpanderPanelContent(fleet)}
      </ExpanderPanel>
    );
  };

  const [grouped, setGrouped] = useState<[string, IVehicle[]][]>();
  const [groupedAll, setAllGrouped] = useState<[string, IVehicle[]][]>();
  const [selected, setSelected] = useState("");
  const [name, setName] = useState("");
  const [selectedTree, setSelectedTree] = useState("");
  const [filterAll, setFilterAll] = useState(false);

  const [directions, setDirections] = useState({
    column: "type",
    asc: true,
  });

  const [assetsCounts, setAssetsCounts] = useState({
    truck: 0,
    trailer: 0,
    bus: 0,
    van: 0,
    driver: 0,
  });

  const [openPanelIndex, setOpenPanelIndex] = useState<number | null>(null);
  const [groupPagination, setGroupPagination] = useState<{
    [key: string]: {
      page: number;
      pageSize: number;
      totalPages: number;
      totalVehicles: number;
      vehicles: IVehicle[];
      loading: boolean;
      hasMore?: boolean;
    }
  }>({});

  const [groupTagMapping, setGroupTagMapping] = useState<{ [key: string]: string }>({});
  const [groupTotalVehicles, setGroupTotalVehicles] = useState<{ [key: string]: number }>({});




  const mountVehicleWithBrand = () => {

    const group = Object.entries(
      vehicles?.reduce<Record<string, IVehicle[]>>((acc, vehicle) => {
        const embeddedData = vehicle.embedded?.map((tag) => fleets?.[tag]) || [];

        if (embeddedData.length === 0) {
          if (!acc["outros"]) {
            acc["outros"] = [];
          }
          acc["outros"].push(vehicle);
        } else {
          embeddedData.forEach((data) => {
            if (data && typeof data === "string") {
              if (!acc[data]) {
                acc[data] = [];
              }
              acc[data].push(vehicle);
            }
          });
        }

        return acc;
      }, {})
    );


    setGrouped(group);
    setAllGrouped(group);
  };

  const header = [
    {
      name: "type",
      label: "tipo",
    },
    {
      name: "vehicle",
      label: "Veículo",
    },
    {
      name: "odometer",
      label: "Odômetro",
    },
    {
      name: "hourmeter",
      label: "Horímetro",
    },
    {
      name: "location",
      label: "localização",
    },
  ];

  const handleSelectTypeTree = (type: string) => {
    setSelected("");
    setName("");

    if (selectedTree === type) {
      setSelectedTree("");
      setFilterAll(false);
      if (onFilterByType) {
        onFilterByType(null);
      }
    } else {
      setSelectedTree(type);
      setFilterAll(false);
      if (onFilterByType) {
        onFilterByType(type);
      }
    }
  };

  const handleFilterAll = () => {
    const active = !filterAll;
    setFilterAll(active);

    if (active) {
      setSelectedTree("");
      if (onFilterByType) {
        onFilterByType(null);
      }
    } else {
      if (onFilterByType) {
        onFilterByType(null);
      }
    }
  };

  const { idVehicleSelected, nameVehicleSelected }: any = useAppSelector((state) => state.trips);
  const { totalByVehicleType } = useAppSelector((state) => state.fleet);


  const fetchVehiclesForTag = async (tagId: string, tagName: string, page: number = 1, pageSize: number = 10, searchText?: string) => {
    if (page < 2) {
      return;
    }

    try {
      setGroupPagination(prev => ({
        ...prev,
        [tagName]: {
          ...prev[tagName],
          loading: true
        }
      }));

      const response = await fetchVehiclesWithTags(
        tagId,
        page,
        pageSize,
        undefined,
        currentVehicleTypeFilter || undefined,
        searchText || inputValue || undefined
      );

      if (response.data?.result) {
        const result = response.data.result;


        setGroupPagination(prev => {
          const previousVehicles = prev[tagName]?.vehicles || [];
          const newVehicles = result.vehicles || [];

          return {
            ...prev,
            [tagName]: {
              page: result.currentPage || page,
              pageSize: pageSize,
              totalPages: prev[tagName]?.totalPages || result.totalPages || 1,
              totalVehicles: prev[tagName]?.totalVehicles || result.totalVehicles || 0,
              vehicles: [...previousVehicles, ...newVehicles],
              loading: false,
              hasMore: (result.currentPage || page) < (prev[tagName]?.totalPages || result.totalPages || 1)
            }
          };
        });
      }
    } catch (error) {
      console.error(`Erro ao buscar ve\u00edculos da tag ${tagName}:`, error);
      setGroupPagination(prev => ({
        ...prev,
        [tagName]: {
          ...prev[tagName],
          loading: false
        }
      }));
    }
  };

  useEffect(() => {
    if (idVehicleSelected && nameVehicleSelected) {
      onVehicleSelectionChange(idVehicleSelected, nameVehicleSelected)

      setSelected(idVehicleSelected);
      setName(nameVehicleSelected);
    }
  }, [idVehicleSelected, nameVehicleSelected])

  useEffect(() => {
    if (vehicles && fleets) {
      mountVehicleWithBrand();

      setGroupPagination({});
      setOpenPanelIndex(null);

      const mapping: { [key: string]: string } = {};
      const totals: { [key: string]: number } = {};


      Object.entries(fleets).forEach(([tagId, tagName]) => {
        mapping[tagName] = tagId;

        if (vehicleTotals && vehicleTotals[tagName] !== undefined) {
          totals[tagName] = vehicleTotals[tagName];
        }
      });


      mapping["outros"] = "00000000-0000-0000-0000-000000000000";
      if (vehicleTotals && vehicleTotals["outros"] !== undefined) {
        totals["outros"] = vehicleTotals["outros"];
      }

      setGroupTagMapping(mapping);
      setGroupTotalVehicles(totals);


      if (totalByVehicleType) {
        setAssetsCounts({
          truck: totalByVehicleType.truck || 0,
          trailer: totalByVehicleType.trailer || 0,
          bus: totalByVehicleType.bus || 0,
          van: totalByVehicleType.van || 0,
          driver: 0,
        });
      } else {

        setAssetsCounts(
          vehicles.reduce(
            (acc, res) => {
              if (typeof res.type === "string" && res.type in acc) {
                acc[res.type as keyof typeof acc]++;
              }
              return acc;
            },
            {
              truck: 0,
              trailer: 0,
              bus: 0,
              van: 0,
              driver: 0,
            }
          )
        );
      }
    }
  }, [vehicles, totalByVehicleType, fleets, vehicleTotals]);

  const groupedMemo = useMemo(() => {
    if (!vehicles || !fleets) return [];
    
    const group = Object.entries(
      vehicles?.reduce<Record<string, IVehicle[]>>((acc, vehicle) => {
        const embeddedData = vehicle.embedded?.map((tag) => fleets?.[tag]) || [];

        if (embeddedData.length === 0) {
          if (!acc["outros"]) {
            acc["outros"] = [];
          }
          acc["outros"].push(vehicle);
        } else {
          embeddedData.forEach((data) => {
            if (data && typeof data === "string") {
              if (!acc[data]) {
                acc[data] = [];
              }
              acc[data].push(vehicle);
            }
          });
        }

        return acc;
      }, {})
    );
    
    return group;
  }, [vehicles, fleets]);

  const handleChangeSelect = (detail: IVehicle) => {
    onVehicleSelectionChange(detail.id, detail.name)
    setSelected(detail.id);
    setName(detail.name);
  }

  const handleDrection = (direction: TDirection) => {
    setDirections(direction);
  }


  useEffect(() => {
    if (openPanelIndex !== null && groupedMemo && groupedMemo[openPanelIndex]) {
      const fleet = groupedMemo[openPanelIndex];
      const groupKey = fleet[0];
      const tagId = groupTagMapping[groupKey];

      if (tagId) {
        const totalVehicles = groupTotalVehicles[groupKey] || fleet[1].length;
        const totalPages = Math.ceil(totalVehicles / 10) || 1;

        setGroupPagination(prev => ({
          ...prev,
          [groupKey]: {
            page: 1,
            pageSize: 10,
            totalPages: totalPages,
            totalVehicles: totalVehicles,
            vehicles: fleet[1].slice(0, 10),
            loading: false,
            hasMore: totalPages > 1
          }
        }));

        if (inputValue) {
          fetchVehiclesForTag(tagId, groupKey, 1, 10, inputValue);
        }
      }
    }
  }, [openPanelIndex, groupedMemo, groupTagMapping, groupTotalVehicles, inputValue]);

  return (
    <div className="bg-white padding-y-20 padding-x-50 height-100pct">
      <div className="display-flex gap-10 align-items-center margin-top-16 margin-bottom-20">
        <div className="width-100pct display-flex align-items-center gap-15 table-toolbar-group-right">
          <div className="width-100pct table-toolbar-column">
            <TableSearch
              value={inputValue}
              onChange={(text) => onChangeInput(text)}
              placeholder="Buscar veículo"
            />
          </div>
          <div className="table-toolbar-column table-toolbar-column table-toolbar-column-spacer">
            <Checkbox onChange={handleFilterAll} checked={filterAll} />
          </div>
          <div className="table-toolbar-column">
            <TreeSummary className="text-color-black">
              <TreeSummaryRow>
                <TypeCounter
                  key={"truck"}
                  type={"truck"}
                  icon={`truck-baseline`}
                  value={<span>{assetsCounts.truck}</span>}
                  isActive={filterAll || selectedTree === "truck"}
                  hasFilter={true}
                  onClick={handleSelectTypeTree}
                  enableActivity
                />
                <TypeCounter
                  key={"bus"}
                  type={"bus"}
                  icon={`bus-baseline`}
                  value={<span>{assetsCounts.bus}</span>}
                  isActive={filterAll || selectedTree === "bus"}
                  hasFilter={true}
                  onClick={handleSelectTypeTree}
                  enableActivity
                />
                <TypeCounter
                  key={"van"}
                  type={"van"}
                  icon={`van-baseline`}
                  value={<span>{assetsCounts.van}</span>}
                  isActive={filterAll || selectedTree === "van"}
                  hasFilter={true}
                  onClick={handleSelectTypeTree}
                  enableActivity
                />
                <TypeCounter
                  key={"trailer"}
                  type={"trailer"}
                  icon={`trailer-baseline`}
                  value={<span>{assetsCounts.trailer}</span>}
                  isActive={filterAll || selectedTree === "trailer"}
                  hasFilter={true}
                  onClick={handleSelectTypeTree}
                  enableActivity
                />
              </TreeSummaryRow>
            </TreeSummary>
          </div>
        </div>
      </div>
      <div>
        <div className="margin-y-15">
          <span className="text-size-14 text-medium line-height-20 text-color-black">Todos os veículos</span>
        </div>
        {!searchLoading && groupedMemo && groupedMemo.length > 0 ? (
          groupedMemo.map((fleet, idx) => renderVehicleExpanderPanel(fleet, idx))
        ) : null}

        {isLoading ? Array.from({
          length: 5,
        }).map((_) => {
          return (
            <ExpanderPanel
              open={false}
              headerClassName="padding-0 position-relative"
              titleClassName="width-100pct border-radius-2 height-60 display-flex border-radius-none"
              bodyClassName="padding-0 height-100pct"
              className="shadow-default margin-bottom-4 border-radius-none  text-color-black"
              iconClassName="position-absolute top-0 right-15 height-100pct display-flex align-items-center text-color-dark"
              title={
                <table className="table table-layout-fixed width-100pct rounded-small">
                  <thead>
                    <tr>
                      <td colSpan={7}>
                        <ContentLoader width='20%' className='height-20' />
                      </td>
                      <td>
                        <div
                          className="bg-white width-45 height-23 display-flex rounded"
                        >
                          <ContentLoader width='100%' className='height-23' />
                        </div>
                      </td>
                    </tr>
                  </thead>
                </table>
              }
            />
          )
        }) : null}

        {!isLoading && groupedMemo?.length === 0 ? (
          <NotFoundState
            headline="Nenhum veículo disponível"
            message="Volte e tente novamente."
            fullWidth
            outerClassName="border-none"
          />
        ) : null}

        <div className="display-flex justify-content-between align-items-center">
          <div className="display-flex width-200">
          </div>
          <div className="display-flex justify-content-center">
            {!isLoading && <Paginator
              pageSize={pageSize}
              handlePage={handlePage}
              page={page}
              pages={pages || 1}
              disabled={grouped?.length == 0}
            />}
          </div>
          <div className="display-flex gap-10">
            <Button
              bsSize={Button.MD}
              className="width-84"
              onClick={() => {
                setSelected("");
                setName("");
                handleCancel();
              }}
            >
              Voltar
            </Button>
            <Button
              disabled={!selected}
              bsStyle={Button.PRIMARY}
              bsSize={Button.MD}
              className="width-84"
              onClick={() => handleSaveVehicle(selected, name)}
            >
              Avançar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddVehicle;